import { useEffect, useRef } from "react";

const MODAL_COPY = {
  en: {
    delivered: "Delivered",
    sharedWithYou: "Shared with you",
    afterReleaseChecks: "After your release checks",
    deliverNow: "Deliver now",
    deliveredPrivacy: "Delivered through your chosen sharing flow",
    sharedPrivacy: "Shared securely with your account",
    privateUntilRelease: "Private until your release settings allow access",
    privateUnlessDeliver: "Private unless you choose to deliver it",
    dialogAria: "Private message review",
    closeAria: "Close private message review",
    privateMessage: "Private message",
    privateMessageDetails: "Private message details",
    detailsAria: "Private message details",
    lengthUnavailable: "Length not available",
    banner: "This message stays private unless your release settings allow access.",
    contextNote: "Context note",
    noNote: "No additional note was added for this message.",
    recipient: "Recipient",
    deliveryStatus: "Delivery status",
    saved: "Saved",
    size: "Size",
    privacy: "Privacy",
    linkedJournal: "Linked journal",
    notLinked: "Not linked",
    playbackUnavailable: "Playback is not available right now",
    openRelatedJournal: "Open related journal",
    openNewTab: "Open in new tab",
    close: "Close",
  },
  fr: {
    delivered: "Transmis",
    sharedWithYou: "Partagé avec vous",
    afterReleaseChecks: "Après vos vérifications de diffusion",
    deliverNow: "Transmettre maintenant",
    deliveredPrivacy: "Transmis selon le parcours de partage choisi",
    sharedPrivacy: "Partagé de façon sécurisée avec votre compte",
    privateUntilRelease: "Privé jusqu'à ce que vos réglages de diffusion autorisent l'accès",
    privateUnlessDeliver: "Privé tant que vous ne choisissez pas de le transmettre",
    dialogAria: "Revue du message privé",
    closeAria: "Fermer la revue du message privé",
    privateMessage: "Message privé",
    privateMessageDetails: "Détails du message privé",
    detailsAria: "Détails du message privé",
    lengthUnavailable: "Durée non disponible",
    banner: "Ce message reste privé tant que vos réglages de diffusion n'autorisent pas l'accès.",
    contextNote: "Note de contexte",
    noNote: "Aucune note supplémentaire n'a été ajoutée à ce message.",
    recipient: "Destinataire",
    deliveryStatus: "Statut de transmission",
    saved: "Enregistré",
    size: "Taille",
    privacy: "Confidentialité",
    linkedJournal: "Journal lié",
    notLinked: "Aucun lien",
    playbackUnavailable: "La lecture n'est pas disponible pour le moment",
    openRelatedJournal: "Ouvrir le journal lié",
    openNewTab: "Ouvrir dans un nouvel onglet",
    close: "Fermer",
  },
};

function trapFocus(event, container) {
  if (event.key !== "Tab" || !container) {
    return;
  }
  const focusable = Array.from(
    container.querySelectorAll(
      'button:not([disabled]), [href], video[controls], [tabindex]:not([tabindex="-1"])'
    )
  );
  if (!focusable.length) {
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function ConfessionReviewModal({
  clip,
  durationLabel,
  journalTitle,
  recipientLabel,
  locale = "en",
  onClose,
  onOpenRelatedJournal,
}) {
  const copy = MODAL_COPY[locale === "fr" ? "fr" : "en"];
  const containerRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      trapFocus(event, containerRef.current);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const deliveryLabel = clip.sentAt || clip.deliveredAt || clip.deliveryStatus === "sent"
    ? copy.delivered
    : clip.isSharedWithMe
      ? copy.sharedWithYou
    : clip.recipientAccessAfterRelease
      ? copy.afterReleaseChecks
      : copy.deliverNow;

  const privacyLabel = clip.sentAt || clip.deliveredAt || clip.deliveryStatus === "sent"
    ? copy.deliveredPrivacy
    : clip.isSharedWithMe
      ? copy.sharedPrivacy
    : clip.recipientAccessAfterRelease
      ? copy.privateUntilRelease
      : copy.privateUnlessDeliver;

  return (
    <div
      className="confession-player-modal"
      role="dialog"
      aria-modal="true"
      aria-label={copy.dialogAria}
      onClick={onClose}
    >
      <div
        ref={containerRef}
        className="confession-player-modal-inner"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="confession-player-topbar">
          <div>
            <p className="journal-section-kicker">{copy.privateMessage}</p>
            <h4>{clip.title}</h4>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="confession-player-close"
            aria-label={copy.closeAria}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="confession-player-stage">
          {clip.viewUrl ? (
            <video src={clip.viewUrl} controls autoPlay className="confession-player-video" />
          ) : (
            <div className="confession-player-video confession-saved-fallback">
              {copy.playbackUnavailable}
            </div>
          )}
        </div>

        <div className="confession-player-content">
          <section className="confession-player-summary">
            <div className="confession-player-summary-head">
              <div>
                <p className="journal-section-kicker">{copy.privateMessageDetails}</p>
                <h5>{clip.title}</h5>
              </div>
              <div className="confession-player-detail-chips" aria-label={copy.detailsAria}>
                <span className="confession-player-detail-chip">{recipientLabel}</span>
                <span className="confession-player-detail-chip">{deliveryLabel}</span>
                <span className="confession-player-detail-chip">{durationLabel || copy.lengthUnavailable}</span>
                <span className="confession-player-detail-chip">{clip.displaySavedAt}</span>
              </div>
            </div>

            <div className="confession-player-banner">
              {copy.banner}
            </div>

            <div className="confession-player-note">
              <strong>{copy.contextNote}</strong>
              <p>{clip.contextNote || copy.noNote}</p>
            </div>

            <div className="confession-player-meta-grid">
              <div>
                <span className="confession-player-label">{copy.recipient}</span>
                <strong>{recipientLabel}</strong>
              </div>
              <div>
                <span className="confession-player-label">{copy.deliveryStatus}</span>
                <strong>{deliveryLabel}</strong>
              </div>
              <div>
                <span className="confession-player-label">{copy.saved}</span>
                <strong>{clip.displaySavedAt}</strong>
              </div>
              <div>
                <span className="confession-player-label">{copy.size}</span>
                <strong>{clip.sizeLabel}</strong>
              </div>
              <div>
                <span className="confession-player-label">{copy.privacy}</span>
                <strong>{privacyLabel}</strong>
              </div>
              <div>
                <span className="confession-player-label">{copy.linkedJournal}</span>
                <strong>{journalTitle || copy.notLinked}</strong>
              </div>
            </div>

            <div className="confession-player-actions">
              {clip.linkedJournalId && onOpenRelatedJournal ? (
                <button type="button" className="secondary" onClick={() => onOpenRelatedJournal(clip.linkedJournalId)}>
                  {copy.openRelatedJournal}
                </button>
              ) : null}
              {clip.viewUrl ? (
                <a href={clip.viewUrl} target="_blank" rel="noreferrer">
                  {copy.openNewTab}
                </a>
              ) : null}
              <button type="button" className="secondary" onClick={onClose}>
                {copy.close}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
